import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import OnboardingCard from './OnboardingCard';
import { FaLightbulb, FaSearch, FaTools, FaGlobeAmericas, FaHistory, FaRocket, FaUsers, FaBook, FaShieldAlt, FaExclamationTriangle, FaLock, FaMicrophone } from 'react-icons/fa';
import { ShieldCheck } from 'lucide-react'; 
import { useOnboardingHighlight } from '@/contexts/OnboardingHighlightContext'; 

interface OnboardingStep {
  id: number;
  title: string;
  message: string;
  visual?: React.ReactNode;
  highlightSidebarPath?: string; 
}

const onboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    title: 'Welcome to Truth Nexus AI!',
    message: 'Your AI-powered companion for navigating the digital world and verifying information. Let\'s get you started.',
    visual: <FaRocket />,
  },
  {
    id: 2,
    title: 'Your Dashboard: The Globe',
    message: 'The Dashboard features an interactive globe to visualize information spread and global discussions. Get a bird\s-eye view!',
    visual: <FaGlobeAmericas />,
    highlightSidebarPath: '/dashboard',
  },
  {
    id: 3,
    title: 'Analyze Content (Detect)',
    message: 'Use the \"Analyze Content\" section to submit text for AI-powered misinformation detection and insights.',
    visual: <FaSearch />,
    highlightSidebarPath: '/detect',
  },
  {
    id: 4,
    title: 'Check Source Credibility',
    message: 'Investigate websites and online sources. The \"Source Credibility\" tool helps you assess their trustworthiness.',
    visual: <FaShieldAlt />,
    highlightSidebarPath: '/sources',
  },
  {
    id: 5,
    title: 'Report Misinformation',
    message: 'Found something suspicious? Use the \"Report Misinfo\" feature to flag it. (Future community feature)',
    visual: <FaExclamationTriangle />,
    highlightSidebarPath: '/report',
  },
  {
    id: 6,
    title: 'Learn with Media Literacy',
    message: 'Boost your critical thinking skills. Our \"Media Literacy\" section offers resources and guides.',
    visual: <FaBook />,
    highlightSidebarPath: '/learn',
  },
  {
    id: 7,
    title: 'OSINT & Investigation Tools',
    message: 'Explore a suite of Open Source Intelligence tools for deeper investigation under \"OSINT Tools\". Build search queries, analyze images, and more!',
    visual: <FaTools />,
    highlightSidebarPath: '/tools',
  },
  {
    id: 8,
    title: 'Join the Community (Chat)',
    message: 'Connect with other users, discuss findings, and share insights in our secure \"Community Chat\".',
    visual: <FaUsers />,
    highlightSidebarPath: '/community',
  },
  {
    id: 9,
    title: 'Privacy & Security Hub',
    message: 'Learn about protecting your digital footprint and enhancing your online security in the \"Privacy & Security\" section.',
    visual: <FaLock />,
    highlightSidebarPath: '/privacy-and-hacking',
  },
  {
    id: 10,
    title: 'Voice-Activated Agent',
    message: 'Interact with Truth Nexus AI hands-free using our \"Voice Agent\". Ask questions and get quick analyses.',
    visual: <FaMicrophone />,
    highlightSidebarPath: '/voice-agent',
  },
  {
    id: 11,
    title: 'Track Your Analysis History',
    message: 'All your analyses are automatically saved to your personal history, accessible again via \"Source Credibility\" for now, reflecting past analyses.',
    visual: <FaHistory />,
    highlightSidebarPath: '/sources', 
  },
  {
    id: 12,
    title: "You're All Set!",
    message: 'Dive in and start exploring. Your journey to digital clarity begins now.',
    visual: <ShieldCheck size={48} className="text-cyan-400" />,
  },
];

interface OnboardingFlowProps {
  onOnboardingComplete: () => void;
}

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onOnboardingComplete }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState(1); 
  const { setHighlightedPath } = useOnboardingHighlight(); 

  const currentStepData = onboardingSteps[currentStepIndex];

  useEffect(() => {
    if (currentStepData.highlightSidebarPath) {
      setHighlightedPath(currentStepData.highlightSidebarPath);
    } else {
      setHighlightedPath(null); 
    }
    return () => setHighlightedPath(null); 
  }, [currentStepIndex, setHighlightedPath, currentStepData.highlightSidebarPath]);

  const handleNext = () => {
    setDirection(1);
    setCurrentStepIndex((prev) => {
      const nextIndex = Math.min(prev + 1, onboardingSteps.length - 1);
      return nextIndex;
    });
  };

  const handleSkip = () => {
    setHighlightedPath(null); 
    onOnboardingComplete();
  };

  const handleFinish = () => {
    setHighlightedPath(null); 
    onOnboardingComplete();
  };


  return (
    <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <AnimatePresence initial={false} custom={direction} mode="wait">
        <OnboardingCard
          key={currentStepData.id} 
          step={currentStepData.id}
          totalSteps={onboardingSteps.length}
          title={currentStepData.title}
          message={currentStepData.message}
          visual={currentStepData.visual}
          onNext={handleNext}
          onSkip={currentStepData.id < onboardingSteps.length ? handleSkip : undefined}
          onFinish={handleFinish}
        />
      </AnimatePresence>
    </div>
  );
};

export default OnboardingFlow;
